import { useState, useCallback } from "react";
import { useRecoilState } from "recoil";
import { navbarNotificationsAtom } from "../../store/navigation";
import { notificationService } from "../../services/notifications/notificationService";
import Swal from "sweetalert2";
import { triggerSnackbar } from "@/components/toastMessage";

/**
 * Single hook for all notification operations.
 *
 * "received" notifications live in the shared Recoil atom so every consumer
 * (ModernNavbar, SidebarNavbar, NotificationsPage) sees the same list and
 * any read/delete is reflected everywhere instantly — no sync effects needed.
 *
 * "sent" notifications use local state (`sentData`) since they are only
 * shown on the full notifications page.
 */
export const useNotification = (userId) => {
  const [notifications, setNotifications] = useRecoilState(
    navbarNotificationsAtom,
  );
  const [sentData, setSentData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchNotifications = useCallback(
    (type) => {
      if (!userId) {
        setError("User ID is required to fetch notifications");
        return;
      }

      if (!type || (type !== "received" && type !== "sent")) {
        setError("Invalid notification type");
        return;
      }

      setLoading(true);
      setError(null);

      notificationService
        .getNotifications(userId, type)
        .then((res) => {
          if (!res || !res.data) {
            setError("Could not load your notifications. Please try again.");
            triggerSnackbar(
              "Could not load your notifications. Please try again.",
              "error",
            );
            if (type === "received") setNotifications([]);
            else setSentData([]);
            return;
          }

          if (res.data.errorCode) {
            const errorMsg =
              res.data.errorMessage ||
              "Could not load your notifications. Please try again.";
            setError(errorMsg);
            triggerSnackbar(errorMsg, "error");
            if (type === "received") setNotifications([]);
            else setSentData([]);
          } else {
            const data = Array.isArray(res.data) ? res.data : [];
            if (type === "received") setNotifications(data);
            else setSentData(data);
            setError(null);
          }
        })
        .catch((err) => {
          const errorMsg =
            err?.customErrorMessage ||
            err?.message ||
            "Unable to load your notifications. Please check your connection and try again.";
          setError(errorMsg);
          triggerSnackbar(errorMsg, "error");
          if (type === "received") setNotifications([]);
          else setSentData([]);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [userId, setNotifications],
  );

  const markNotificationAsRead = useCallback(
    async (notificationPk) => {
      if (!notificationPk) {
        console.error("Notification PK is required");
        return false;
      }

      try {
        await notificationService.markNotificationRead(notificationPk, true);
        setNotifications((prev) =>
          prev.map((n) =>
            n.pk === notificationPk ? { ...n, isRead: true } : n,
          ),
        );
        return true;
      } catch (err) {
        console.error("Failed to mark notification as read:", err);
        return false;
      }
    },
    [setNotifications],
  );

  const markAllNotificationsAsRead = useCallback(async () => {
    setNotifications((prev) => {
      const unread = prev.filter((n) => !n.isRead);
      if (!unread.length) return prev;

      // Fire API calls (fire-and-forget from state perspective)
      Promise.allSettled(
        unread.map((n) =>
          notificationService.markNotificationRead(n.pk, true),
        ),
      );

      return prev.map((n) => ({ ...n, isRead: true }));
    });
  }, [setNotifications]);

  const deleteNotification = useCallback(
    (notificationPk, activeMenuItem) => {
      if (!notificationPk) {
        triggerSnackbar("Notification ID is required", "error");
        return Promise.reject(new Error("Notification PK is required"));
      }

      if (!activeMenuItem) {
        triggerSnackbar("Active menu item is required", "error");
        return Promise.reject(new Error("Active menu item is required"));
      }

      return new Promise((resolve, reject) => {
        Swal.fire({
          title: "Delete this notification?",
          text: "This action cannot be undone.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          confirmButtonText: "Yes, delete it",
          cancelButtonText: "Cancel",
          customClass: {
            container: "Swal.fire-high-z-index",
          },
          didOpen: () => {
            const swalContainer = document.querySelector(".swal2-container");
            if (swalContainer) {
              swalContainer.style.zIndex = "10000";
            }
          },
        }).then((result) => {
          if (result.isConfirmed) {
            setLoading(true);
            setError(null);

            notificationService
              .deleteNotification(notificationPk)
              .then((res) => {
                if (!res || !res.data) {
                  const errorMsg = "Something went wrong. Please try again.";
                  setError(errorMsg);
                  triggerSnackbar(errorMsg, "error");
                  reject(new Error(errorMsg));
                  return;
                }

                if (res.data.errorCode !== 200) {
                  const errorMsg =
                    res.data.errorMessage ||
                    "Could not delete the notification. Please try again.";
                  setError(errorMsg);
                  triggerSnackbar(errorMsg, "error");
                  reject(new Error(errorMsg));
                } else {
                  const successMsg =
                    res.data.errorMessage ||
                    "Notification deleted successfully";
                  triggerSnackbar(successMsg, "success");
                  setError(null);

                  // Optimistically remove from state
                  if (activeMenuItem === "received") {
                    setNotifications((prev) =>
                      prev.filter((n) => n.pk !== notificationPk),
                    );
                  } else {
                    setSentData((prev) =>
                      prev.filter((n) => n.pk !== notificationPk),
                    );
                  }
                  resolve();
                }
              })
              .catch((err) => {
                const errorMsg =
                  err?.customErrorMessage ||
                  err?.message ||
                  "Could not delete the notification. Please check your connection and try again.";
                setError(errorMsg);
                triggerSnackbar(errorMsg, "error");
                reject(err);
              })
              .finally(() => {
                setLoading(false);
              });
          } else {
            reject(new Error("Deletion cancelled"));
          }
        });
      });
    },
    [setNotifications],
  );

  return {
    notifications,
    sentData,
    loading,
    error,
    fetchNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    deleteNotification,
  };
};
